import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { QrCodeIcon, CreditCardIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, SearchIcon, RefreshCwIcon, ShieldAlertIcon } from 'lucide-react';
export function SmartIdRfid() {
  const [activeTab, setActiveTab] = useState<'cards' | 'issue' | 'verify' | 'reissue'>('cards');
  const [currentStep, setCurrentStep] = useState(0);
  const [verificationResult, setVerificationResult] = useState<'valid' | 'invalid' | null>(null);
  // Re-issue state
  const [reissueSearch, setReissueSearch] = useState('');
  const [reissueFound, setReissueFound] = useState<any | null>(null);
  const [reissueReason, setReissueReason] = useState('lost');
  const [reissueConfirmed, setReissueConfirmed] = useState(false);
  const [cardsList, setCardsList] = useState([{
    cardId: 'CARD-2024-001',
    name: 'Rahim Uddin',
    nid: '1234567890123',
    type: 'QR + RFID',
    issueDate: '2024-01-15',
    expiry: '2025-01-15',
    status: 'active' as const
  }, {
    cardId: 'CARD-2024-002',
    name: 'Karim Sheikh',
    nid: '2345678901234',
    type: 'QR Code',
    issueDate: '2024-02-10',
    expiry: '2025-02-10',
    status: 'active' as const
  }, {
    cardId: 'CARD-2024-003',
    name: 'Fatema Begum',
    nid: '3456789012345',
    type: 'RFID',
    issueDate: '2024-02-20',
    expiry: '2025-02-20',
    status: 'active' as const
  }, {
    cardId: 'CARD-2024-004',
    name: 'Abdul Hakim',
    nid: '4567890123456',
    type: 'QR + RFID',
    issueDate: '2023-12-01',
    expiry: '2024-12-01',
    status: 'expired' as const
  }, {
    cardId: 'CARD-2024-005',
    name: 'Jamal Hossain',
    nid: '5678901234567',
    type: 'QR Code',
    issueDate: '2024-01-20',
    expiry: '2024-03-15',
    status: 'lost' as const
  }]);
  const columns = [{
    key: 'cardId',
    label: 'Card ID'
  }, {
    key: 'name',
    label: 'Beneficiary Name'
  }, {
    key: 'nid',
    label: 'NID'
  }, {
    key: 'type',
    label: 'Card Type'
  }, {
    key: 'issueDate',
    label: 'Issue Date'
  }, {
    key: 'expiry',
    label: 'Expiry'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-2401" data-mp="23cbopswcxbiy" />
  }];
  const filters = [{
    key: 'type',
    label: 'Card Type',
    options: ['QR Code', 'RFID', 'QR + RFID']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Active', 'Expired', 'Lost', 'Replaced']
  }];
  const wizardSteps = [{
    title: 'Select Beneficiary',
    description: 'Choose approved beneficiary'
  }, {
    title: 'Card Configuration',
    description: 'Configure card type and settings'
  }, {
    title: 'Review & Print',
    description: 'Review and print the card'
  }];
  const handleReissueSearch = () => {
    const found = cardsList.find(c => c.cardId.toLowerCase() === reissueSearch.toLowerCase() || c.nid === reissueSearch);
    setReissueFound(found || 'not_found');
    setReissueConfirmed(false);
  };
  const handleReissue = () => {
    if (!reissueFound || reissueFound === 'not_found') return;
    // Mark old card as lost/damaged
    const updatedCards = cardsList.map(c => c.cardId === reissueFound.cardId ? {
      ...c,
      status: reissueReason === 'lost' ? 'lost' as const : 'expired' as const
    } : c);
    // Create new replacement card
    const newCardId = `CARD-${new Date().getFullYear()}-${String(cardsList.length + 1).padStart(3, '0')}`;
    const today = new Date().toISOString().split('T')[0];
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    const newCard = {
      cardId: newCardId,
      name: reissueFound.name,
      nid: reissueFound.nid,
      type: reissueFound.type,
      issueDate: today,
      expiry: expiryDate.toISOString().split('T')[0],
      status: 'active' as const
    };
    setCardsList([newCard, ...updatedCards]);
    setReissueConfirmed(true);
  };
  const resetReissue = () => {
    setReissueSearch('');
    setReissueFound(null);
    setReissueConfirmed(false);
    setReissueReason('lost');
  };
  return <div className="space-y-6" data-id="element-2402" data-mp="sjziqx2pu6ts">
      <div data-id="element-2403" data-mp="erjexu1zq4rt">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2404" data-mp="errr1jqah61y">Smart ID & RFID</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2405" data-mp="irtx05324ln1">
          Manage smart ID cards and RFID verification
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-2406" data-mp="4tcaubssvh78">
        <div className="flex gap-4" data-id="element-2407" data-mp="9s6qx5zdurnn">
          <button onClick={() => setActiveTab('cards')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'cards' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2408" data-mp="9r4356xcx5by">
            Card Registry
          </button>
          <button onClick={() => setActiveTab('issue')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'issue' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2409" data-mp="1origauivra">
            Issue New Card
          </button>
          <button onClick={() => {
          setActiveTab('reissue');
          resetReissue();
        }} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'reissue' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2410" data-mp="iv2fu2k203xx">
            Lost Card / Re-issue
          </button>
          <button onClick={() => setActiveTab('verify')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'verify' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2411" data-mp="uxc4ug6ucygd">
            Verify Card
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'cards' && <DataTable columns={columns} data={cardsList} filters={filters} onDataChange={setCardsList} searchPlaceholder="Search cards..." data-id="element-2412" data-mp="1do39x5jsq2oy" />}

      {activeTab === 'issue' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      alert('Card issued successfully!');
      setCurrentStep(0);
      setActiveTab('cards');
    }} data-id="element-2413" data-mp="1nm98peab2spl">
          {currentStep === 0 && <div className="space-y-4" data-id="element-2414" data-mp="2bgt4yhzzh9ax">
              <div data-id="element-2415" data-mp="2bhushly6ro9j">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2416" data-mp="2bidyc4akam77">
                  Search Approved Beneficiary *
                </label>
                <input type="text" placeholder="Enter NID or Name" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2417" data-mp="22o9bdvyjtuwy" />
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2418" data-mp="2udckbp1drtfx">
                <div className="flex items-center gap-4" data-id="element-2419" data-mp="2qev84r33rsrn">
                  <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center text-2xl" data-id="element-2420" data-mp="2le7kmfe31u26">
                    👤
                  </div>
                  <div className="flex-1" data-id="element-2421" data-mp="2gia1y2fckbm8">
                    <h4 className="font-semibold text-gray-900" data-id="element-2422" data-mp="1a0vchsbaipa7">Rahim Uddin</h4>
                    <div className="text-sm text-gray-600 mt-1" data-id="element-2423" data-mp="1507pdhq6msl9">
                      <div data-id="element-2424" data-mp="1533mj3u7hd8n">NID: 1234567890123</div>
                      <div data-id="element-2425" data-mp="351yszfwqr0ik">Profession: Jele (Fisher)</div>
                      <div data-id="element-2426" data-mp="3a23btcl4g7by">
                        Status: <StatusBadge status="approved" data-id="element-2427" data-mp="3a2d10xyceagl" />
                      </div>
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-2428" data-mp="2v6ne8xlc1tat">
                    Select
                  </button>
                </div>
              </div>
            </div>}
          {currentStep === 1 && <div className="space-y-4" data-id="element-2429" data-mp="3ssuiu7hmzln6">
              <div data-id="element-2430" data-mp="3srt33ppoeaz4">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2431" data-mp="3suffc2niheno">
                  Card Type *
                </label>
                <div className="grid grid-cols-1 max-w-xs" data-id="element-2432" data-mp="3j1zsos19s1wg">
                  <label className="border-2 border-[#1B5E20] bg-green-50 rounded-lg p-4 cursor-pointer" data-id="element-2433" data-mp="3j0f037yimt18">
                    <input type="radio" name="cardType" value="rfid" className="sr-only" defaultChecked data-id="element-2434" data-mp="fdog9a49d74l" />
                    <div className="text-center" data-id="element-2435" data-mp="1v3z3dlct0oez">
                      <CreditCardIcon className="mx-auto mb-2 text-[#1B5E20]" size={32} data-id="element-2436" data-mp="1v363n4c9iz6c" />
                      <div className="font-medium" data-id="element-2437" data-mp="24w4rul3uknpf">RFID</div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="bg-gray-100 rounded-lg p-6 border-2 border-dashed border-gray-300" data-id="element-2438" data-mp="1c64nqoh67enm">
                <div className="text-center text-gray-600" data-id="element-2439" data-mp="1q5xlh3r46v53">
                  <div className="text-sm font-medium mb-2" data-id="element-2440" data-mp="1q7gymlhh4q2n">
                    Card Template Preview
                  </div>
                  <div className="bg-white rounded-lg p-4 inline-block" data-id="element-2441" data-mp="1m6oqxwo92t8h">
                    <div className="w-64 h-40 border-2 border-gray-300 rounded-lg flex items-center justify-center" data-id="element-2442" data-mp="31j9gqq8t65fn">
                      <div className="text-center" data-id="element-2443" data-mp="2xltvz7but3v9">
                        <div className="font-bold text-lg mb-2" data-id="element-2444" data-mp="1df5i31dvxqp1">
                          Bangladesh Forest Dept.
                        </div>
                        <div className="text-sm" data-id="element-2445" data-mp="19gzd9ywlxl6r">Rahim Uddin</div>
                        <div className="text-xs text-gray-600 mt-1" data-id="element-2446" data-mp="14mlo4e8jgaaj">
                          ID: CARD-2024-XXX
                        </div>
                        <div className="mt-3 w-20 h-20 bg-gray-200 mx-auto" data-id="element-2447" data-mp="uppeg56wli4e"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-6" data-id="element-2448" data-mp="lpkhyq390450">
              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2449" data-mp="gpxvdq1j5bjj">
                <h4 className="font-semibold text-gray-900 mb-4" data-id="element-2450" data-mp="griqbgrrawhx">
                  Card Preview
                </h4>
                <div className="space-y-6" data-id="element-2451" data-mp="2g0z8h94327lt">
                  {/* Front Side */}
                  <div data-id="element-2452" data-mp="2b4i9tkz9cauh">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-2453" data-mp="2b24hhjvypcqm">
                      Front Side
                    </p>
                    <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 max-w-2xl mx-auto" data-id="element-2454" data-mp="2nl9v9dh2v4vr">
                      <img src="https://cdn.magicpatterns.com/uploads/pqj4eXKmV692dt1h5NizSK/image.png" alt="Smart ID Card - Front" className="w-full h-auto object-contain" data-id="element-2455" data-mp="2se3001cjqd7u" />
                    </div>
                  </div>
                  {/* Back Side */}
                  <div data-id="element-2456" data-mp="29ma5u3lqqlyj">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-2457" data-mp="29m0f2dsvgars">
                      Back Side
                    </p>
                    <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 max-w-2xl mx-auto" data-id="element-2458" data-mp="3l28lwrfc6899">
                      <img src="https://cdn.magicpatterns.com/uploads/3k2tX1SLhegMkNKt1LT53X/BackCard(1).png" alt="Smart ID Card - Back" className="w-full h-auto object-contain" data-id="element-2459" data-mp="3p30ste0qk12d" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center" data-id="element-2460" data-mp="1yx4dorl30ifr">
                <button className="px-6 py-3 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium" data-id="element-2461" data-mp="1u1gq2gm8zxcd">
                  Print Card
                </button>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'reissue' && <div className="max-w-2xl mx-auto space-y-6" data-id="element-2462" data-mp="1g15yfbd46eq5">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-id="element-2463" data-mp="1g2pccvbasdor">
            <AlertTriangleIcon className="text-amber-600 mt-0.5" size={20} data-id="element-2464" data-mp="1b7t90z0m23wc" />
            <div data-id="element-2465" data-mp="niquw156qt6l">
              <h4 className="font-semibold text-amber-900" data-id="element-2466" data-mp="ni8mk6590oib">
                Lost / Damaged Card Re-issue
              </h4>
              <p className="text-sm text-amber-700 mt-1" data-id="element-2467" data-mp="2u8lp4ulwonop">
                Search for the original card by Card ID or NID. The old card
                will be deactivated and a new replacement card will be issued.
              </p>
            </div>
          </div>

          {!reissueConfirmed && <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4" data-id="element-2468" data-mp="2q6qsi3ex6yh9">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-2469" data-mp="3dwl3sgjlceze">
                Search Original Card
              </h3>
              <div className="flex gap-3" data-id="element-2470" data-mp="3hyofn6a69ll7">
                <div className="flex-1" data-id="element-2471" data-mp="3hxmvmcb6horc">
                  <input type="text" value={reissueSearch} onChange={e => setReissueSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleReissueSearch()} placeholder="Enter Card ID (e.g. CARD-2024-001) or NID" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2472" data-mp="33ynfxu5n8k7b" />
                </div>
                <button onClick={handleReissueSearch} disabled={!reissueSearch.trim()} className="px-5 py-2.5 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2473" data-mp="27zoqu7j4cyz5">
                  <SearchIcon size={16} data-id="element-2474" data-mp="2voq0tz7loi6j" />
                  Search
                </button>
              </div>

              {reissueFound === 'not_found' && <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3" data-id="element-2475" data-mp="2mp2wp5nhky9s">
                  <XCircleIcon className="text-red-600" size={20} data-id="element-2476" data-mp="2hq9lxkzl3ldb" />
                  <p className="text-sm text-red-800 font-medium" data-id="element-2477" data-mp="1bbifl1roz8bw">
                    No card found matching "{reissueSearch}". Please check the
                    Card ID or NID and try again.
                  </p>
                </div>}

              {reissueFound && reissueFound !== 'not_found' && <div className="space-y-4" data-id="element-2478" data-mp="1183kx5oxzu3j">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2479" data-mp="114pixyk6xmwm">
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-2480" data-mp="16426klivyy09">
                      Card Found
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2481" data-mp="1exf8gcyxh9yh">
                      <div data-id="element-2482" data-mp="1jx9zqhmm81k2">
                        <span className="text-gray-500" data-id="element-2483" data-mp="1jwifg2ygxq93">Card ID:</span>
                        <span className="ml-2 font-semibold text-gray-900" data-id="element-2484" data-mp="1owwdqta9pltx">
                          {reissueFound.cardId}
                        </span>
                      </div>
                      <div data-id="element-2485" data-mp="1ttd8i6bz2yfz">
                        <span className="text-gray-500" data-id="element-2486" data-mp="3qoj9t990o6x9">Name:</span>
                        <span className="ml-2 font-semibold text-gray-900" data-id="element-2487" data-mp="3qqcbsb8oq2yx">
                          {reissueFound.name}
                        </span>
                      </div>
                      <div data-id="element-2488" data-mp="3nmn00j8oupxb">
                        <span className="text-gray-500" data-id="element-2489" data-mp="3inblzhzphyjf">NID:</span>
                        <span className="ml-2 font-medium text-gray-900" data-id="element-2490" data-mp="3ip4pwpixq4nq">
                          {reissueFound.nid}
                        </span>
                      </div>
                      <div data-id="element-2491" data-mp="3skom7d1ev5d0">
                        <span className="text-gray-500" data-id="element-2492" data-mp="3snklb4ozw031">Card Type:</span>
                        <span className="ml-2 font-medium text-gray-900" data-id="element-2493" data-mp="aejxgzgd4yj9">
                          {reissueFound.type}
                        </span>
                      </div>
                      <div data-id="element-2494" data-mp="k5xwz78bdyce">
                        <span className="text-gray-500" data-id="element-2495" data-mp="k54skcwtvkxf">Issue Date:</span>
                        <span className="ml-2 text-gray-900" data-id="element-2496" data-mp="f7mjmmit95pf">
                          {reissueFound.issueDate}
                        </span>
                      </div>
                      <div data-id="element-2497" data-mp="p511mqkdu2ju">
                        <span className="text-gray-500" data-id="element-2498" data-mp="p5sjkyl241rn">Current Status:</span>
                        <span className="ml-2" data-id="element-2499" data-mp="17sjnhhcfhmh0">
                          <StatusBadge status={reissueFound.status} data-id="element-2500" data-mp="17tunr76o0c3d" />
                        </span>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-2501" data-mp="yrlf6muyissm">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2502" data-mp="ys3mqfn2ktfu">
                      Reason for Re-issue *
                    </label>
                    <div className="flex gap-4" data-id="element-2503" data-mp="p2blj2kgt22e">
                      <label className={`flex-1 border-2 rounded-lg p-3 cursor-pointer text-center transition-colors ${reissueReason === 'lost' ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2504" data-mp="p0h1rrkxjfzw">
                        <input type="radio" name="reissueReason" value="lost" checked={reissueReason === 'lost'} onChange={() => setReissueReason('lost')} className="sr-only" data-id="element-2505" data-mp="k4a22gj4n0qe" />
                        <AlertTriangleIcon size={20} className={`mx-auto mb-1 ${reissueReason === 'lost' ? 'text-[#1B5E20]' : 'text-gray-400'}`} data-id="element-2506" data-mp="llz1aqspxdwl" />
                        <div className={`text-sm font-medium ${reissueReason === 'lost' ? 'text-[#1B5E20]' : 'text-gray-600'}`} data-id="element-2507" data-mp="cjzgroihvrow">
                          Lost
                        </div>
                      </label>
                      <label className={`flex-1 border-2 rounded-lg p-3 cursor-pointer text-center transition-colors ${reissueReason === 'damaged' ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2508" data-mp="3u199mgn7bfct">
                        <input type="radio" name="reissueReason" value="damaged" checked={reissueReason === 'damaged'} onChange={() => setReissueReason('damaged')} className="sr-only" data-id="element-2509" data-mp="3l2x7bhcz2t4j" />
                        <XCircleIcon size={20} className={`mx-auto mb-1 ${reissueReason === 'damaged' ? 'text-[#1B5E20]' : 'text-gray-400'}`} data-id="element-2510" data-mp="25j7t21hocaxm" />
                        <div className={`text-sm font-medium ${reissueReason === 'damaged' ? 'text-[#1B5E20]' : 'text-gray-600'}`} data-id="element-2511" data-mp="1cv25l6z8k7u3">
                          Damaged
                        </div>
                      </label>
                      <label className={`flex-1 border-2 rounded-lg p-3 cursor-pointer text-center transition-colors ${reissueReason === 'stolen' ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2512" data-mp="1qlff3drrzulj">
                        <input type="radio" name="reissueReason" value="stolen" checked={reissueReason === 'stolen'} onChange={() => setReissueReason('stolen')} className="sr-only" data-id="element-2513" data-mp="5jenvintia11" />
                        <ShieldAlertIcon size={20} className={`mx-auto mb-1 ${reissueReason === 'stolen' ? 'text-[#1B5E20]' : 'text-gray-400'}`} data-id="element-2514" data-mp="jgjx1ritvp0i" />
                        <div className={`text-sm font-medium ${reissueReason === 'stolen' ? 'text-[#1B5E20]' : 'text-gray-600'}`} data-id="element-2515" data-mp="3rypx2nykfiwq">
                          Stolen
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-2516" data-mp="3483nutctrsl5">
                    <p className="text-xs text-red-700" data-id="element-2517" data-mp="2wpw3k8i66gwt">
                      <strong data-id="element-2518" data-mp="2wn9vm7rd9ze2">Warning:</strong> Proceeding will permanently
                      deactivate card <strong data-id="element-2519" data-mp="2rr2mk63oc7rw">{reissueFound.cardId}</strong> and
                      issue a new replacement card. This action cannot be
                      undone.
                    </p>
                  </div>

                  <div className="flex justify-end gap-3" data-id="element-2520" data-mp="2iro1xna6urfb">
                    <button onClick={resetReissue} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-id="element-2521" data-mp="2dra0wp708hqs">
                      Cancel
                    </button>
                    <button onClick={handleReissue} className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2 text-sm" data-id="element-2522" data-mp="1z2dji1j25sdi">
                      <RefreshCwIcon size={16} data-id="element-2523" data-mp="1q1pge1v7dcfe" />
                      Deactivate & Re-issue
                    </button>
                  </div>
                </div>}
            </div>}

          {reissueConfirmed && <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6" data-id="element-2524" data-mp="16fduazz8g6nj">
              <div className="flex items-start gap-4" data-id="element-2525" data-mp="1be786tiflzo8">
                <CheckCircleIcon className="text-green-600 flex-shrink-0" size={48} data-id="element-2526" data-mp="1bf09vg2j9yzi" />
                <div className="flex-1" data-id="element-2527" data-mp="1z65nrpowyh76">
                  <h4 className="text-xl font-bold text-green-900 mb-2" data-id="element-2528" data-mp="241uovxgl065l">
                    Card Re-issued Successfully
                  </h4>
                  <div className="space-y-2 text-sm text-green-900" data-id="element-2529" data-mp="1pbvso08l74sr">
                    <div data-id="element-2530" data-mp="1ubrw9yddgu7s">
                      <span className="font-semibold" data-id="element-2531" data-mp="1ub00amfv4ugz">Old Card:</span>{' '}
                      {reissueFound.cardId} — <StatusBadge status="lost" data-id="element-2532" data-mp="2i0knjhj1udw2" />
                    </div>
                    <div data-id="element-2533" data-mp="2muyl9ql6ox3w">
                      <span className="font-semibold" data-id="element-2534" data-mp="2mvgnd3uj25jq">New Card:</span>{' '}
                      {cardsList[0]?.cardId} — <StatusBadge status="active" data-id="element-2535" data-mp="bc24q41xg0s6" />
                    </div>
                    <div data-id="element-2536" data-mp="6esggrq9xv5x">
                      <span className="font-semibold" data-id="element-2537" data-mp="6dhd2t0qqpfc">Beneficiary:</span>{' '}
                      {reissueFound.name}
                    </div>
                    <div data-id="element-2538" data-mp="g66cgpj0ocub">
                      <span className="font-semibold" data-id="element-2539" data-mp="g5wxdwzifq3e">Reason:</span>{' '}
                      {reissueReason.charAt(0).toUpperCase() + reissueReason.slice(1)}
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3" data-id="element-2540" data-mp="1g7jd16bjrlp">
                    <button onClick={() => {
                resetReissue();
                setActiveTab('cards');
              }} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2541" data-mp="1h0j3i6v1guc">
                      View Card Registry
                    </button>
                    <button onClick={resetReissue} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-id="element-2542" data-mp="237j1puyvj6vi">
                      Re-issue Another
                    </button>
                  </div>
                </div>
              </div>
            </div>}
        </div>}

      {activeTab === 'verify' && <div className="max-w-2xl mx-auto" data-id="element-2543" data-mp="2m392xfyzfz8c">
          <div className="bg-white rounded-lg border border-gray-200 p-8" data-id="element-2544" data-mp="2r3n208ilvyu8">
            <h3 className="text-xl font-semibold text-gray-900 text-center mb-6" data-id="element-2545" data-mp="2vvew2t0z0mp4">
              Verify Smart ID Card
            </h3>

            <div className="space-y-4 mb-8" data-id="element-2546" data-mp="30t6nllxpe6ss">
              <button onClick={() => setVerificationResult('valid')} className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] transition-colors" data-id="element-2547" data-mp="30w2jz5twknf4">
                <QrCodeIcon size={24} data-id="element-2548" data-mp="y8cfh00if5ed" />
                <span className="font-medium" data-id="element-2549" data-mp="y583vhkpt7do">Scan QR Code</span>
              </button>

              <div className="text-center text-sm text-gray-600" data-id="element-2550" data-mp="u8293m8t2cyz">OR</div>

              <input type="text" placeholder="Enter Card ID manually" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2551" data-mp="p7o2m8murb32" />

              <div className="text-center text-sm text-gray-600" data-id="element-2552" data-mp="1cwpd01f8wwaz">OR</div>

              <button onClick={() => setVerificationResult('invalid')} className="w-full flex items-center justify-center gap-3 px-6 py-4 border-2 border-[#1B5E20] text-[#1B5E20] rounded-lg hover:bg-green-50 transition-colors" data-id="element-2553" data-mp="1cyjybgufiqfl">
                <CreditCardIcon size={24} data-id="element-2554" data-mp="20nmf6mh8of91" />
                <span className="font-medium" data-id="element-2555" data-mp="1vvbg1luboxhj">Tap RFID Card</span>
              </button>
            </div>

            {verificationResult && <div className={`rounded-lg p-6 ${verificationResult === 'valid' ? 'bg-green-50 border-2 border-green-500' : 'bg-red-50 border-2 border-red-500'}`} data-id="element-2556" data-mp="3q4gkogl8pj3m">
                <div className="flex items-start gap-4" data-id="element-2557" data-mp="3gcar52fcatmb">
                  {verificationResult === 'valid' ? <CheckCircleIcon className="text-green-600 flex-shrink-0" size={48} data-id="element-2558" data-mp="3lavwthdd25i5" /> : <XCircleIcon className="text-red-600 flex-shrink-0" size={48} data-id="element-2559" data-mp="3bfc04sqz32sc" />}
                  <div className="flex-1" data-id="element-2560" data-mp="32db5m3n4iuud">
                    <h4 className={`text-xl font-bold mb-2 ${verificationResult === 'valid' ? 'text-green-900' : 'text-red-900'}`} data-id="element-2561" data-mp="32edvq9cmgt98">
                      {verificationResult === 'valid' ? 'Valid Card' : 'Invalid Card'}
                    </h4>
                    {verificationResult === 'valid' ? <div className="space-y-2 text-sm text-green-900" data-id="element-2562" data-mp="15scy9p91a8rp">
                        <div data-id="element-2563" data-mp="1jizyjlug06pw">
                          <span className="font-semibold" data-id="element-2564" data-mp="1jj9oj9fhmdvl">Name:</span> Rahim
                          Uddin
                        </div>
                        <div data-id="element-2565" data-mp="1epwz4x2zi31s">
                          <span className="font-semibold" data-id="element-2566" data-mp="1tg4jjicjxw54">Card ID:</span>{' '}
                          CARD-2024-001
                        </div>
                        <div data-id="element-2567" data-mp="1ohb8dwkqmh84">
                          <span className="font-semibold" data-id="element-2568" data-mp="22hc4yztyz2hs">Profession:</span>{' '}
                          Jele (Fisher)
                        </div>
                        <div data-id="element-2569" data-mp="1yh1zrpe04g3z">
                          <span className="font-semibold" data-id="element-2570" data-mp="1ydwd0gsuhqbf">Active Permit:</span>{' '}
                          PER-2024-001 (Fishing, Valid until 2024-04-05)
                        </div>
                        <div data-id="element-2571" data-mp="43iws017c5ba">
                          <span className="font-semibold" data-id="element-2572" data-mp="smo0l1gr0h9j">Vessel Name:</span> MV
                          Sundarban-1
                        </div>
                        <div data-id="element-2573" data-mp="xkfycc42qxln">
                          <span className="font-semibold" data-id="element-2574" data-mp="xjmsddcxwc4k">BLC No:</span>{' '}
                          BLC-2024-8821
                        </div>
                        <div data-id="element-2575" data-mp="16eauwvr4rfpo">
                          <span className="font-semibold" data-id="element-2576" data-mp="16dj6q5zv4k9f">Status:</span>{' '}
                          <StatusBadge status="active" data-id="element-2577" data-mp="9otr6mbpk3qm" />
                        </div>
                      </div> : <div className="text-sm text-red-900" data-id="element-2578" data-mp="eoze5p71922d">
                        <div className="font-semibold mb-1" data-id="element-2579" data-mp="infa4il3dkrx">
                          Reason: Card Expired
                        </div>
                        <div data-id="element-2580" data-mp="nlgs106s7e1t">
                          This card expired on 2024-03-15. Please renew the card
                          before entry.
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}
          </div>
        </div>}
    </div>;
}